// THE PHONE (Stage 4.8f.4a/4.8f.4b, guide §6.12; mockups https://claude.ai/artifact/JUBHBPNAAFERVMJ5GYNYij) — what the person sees,
// docked beside every page (PhoneProvider.jsx holds the line, so minimising or moving between pages never drops a call): a header with
// their status (on a break / do not disturb keep calls from ringing — 4.8f.3) and the line's state; Keypad (smart dialling), Recent
// (kept by the server), Contacts (team, customers, favourites); in a call, the call (mute, hold, keypad, transfer by the engine, a
// second call waiting — F3); and the states where it cannot call (microphone blocked, no secure connection, line reconnecting) —
// found when the phone opens, not when a customer rings. The owner without a line yet is offered theirs (F1).
import { useEffect, useState } from "react";
import { Box, ButtonBase, Menu, MenuItem, Typography, CircularProgress } from "@mui/material";
import { ChevronDown, Wifi, WifiOff, MicOff, RefreshCw, ShieldAlert, Minimize2, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import PhoneKeypad from "./PhoneKeypad";
import PhoneRecent from "./PhoneRecent";
import PhoneBook from "./PhoneBook";
import PhoneCall from "./PhoneCall";
import { Dot, Segments } from "./phoneUi";

const STATUSES = ["available", "break", "dnd", "offline"];
const STATUS_COLOR = { available: "var(--success)", break: "var(--warning)", dnd: "var(--danger)", offline: "var(--ink-400)" };

function Banner({ icon, tone = "warn", title, body, action, onAction, busy }) {
  const Icon = icon;
  const bg = tone === "bad" ? "var(--danger-bg)" : "var(--warning-bg)";
  const fg = tone === "bad" ? "var(--danger)" : "var(--warning)";
  return (
    <Box role="status" sx={{ m: "8px 16px 0", p: "10px 12px", borderRadius: "12px", background: bg, display: "flex", gap: "10px", alignItems: "flex-start" }}>
      <Icon size={18} strokeWidth={1.8} aria-hidden="true" style={{ color: fg, flexShrink: 0, marginTop: 1 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ font: "700 13px/1.35 var(--font-ui)", color: "var(--ink-900)" }}>{title}</Typography>
        {body && <Typography sx={{ font: "500 12px/1.4 var(--font-ui)", color: "var(--ink-600)" }}>{body}</Typography>}
      </Box>
      {action && (
        <ButtonBase onClick={onAction} disabled={busy} sx={{ minHeight: 36, px: "12px", borderRadius: "10px", background: "var(--surface-card)", border: "1px solid var(--line-200)", font: "700 12px/1 var(--font-ui)", color: "var(--ink-900)" }}>
          {action}
        </ButtonBase>
      )}
    </Box>
  );
}

export default function PhoneDock({ line, me, ready, status = "available", onStatusChange, statusSaving = false, recent, ringName, onOpenTicket, onMinimise, prefill, onMakeLine, making, makeError }) {
  const { t } = useTranslation("phone");
  const [tab, setTab] = useState("keypad");
  const [statusMenu, setStatusMenu] = useState(null);
  const [checkingMic, setCheckingMic] = useState(false);

  // A number sent here (click-to-call that could not dial at once): the keypad, with it.
  useEffect(() => { if (prefill?.seq) setTab("keypad"); }, [prefill?.seq]);

  const hasLine = !!me?.extension;
  const current = line.calls.find((c) => c.state === "active") ?? line.calls.find((c) => c.state === "calling") ?? line.calls.find((c) => c.state === "held") ?? null;
  const today = new Date().toDateString();
  const missed = (recent?.calls ?? []).filter((c) => c.dir === "missed" && c.at && new Date(c.at).toDateString() === today).length;
  const dial = (number, name) => { if (ready) line.dial(number, name); };
  const recheck = async () => { setCheckingMic(true); await line.checkMic(true); setCheckingMic(false); };

  const secure = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  const conn = !hasLine ? { Icon: WifiOff, color: "var(--ink-400)", text: t("noLineShort") }
    : line.reg === "registered" ? { Icon: Wifi, color: "var(--success)", text: t("connected") }
    : line.reg === "failed" ? { Icon: WifiOff, color: "var(--danger)", text: t("notConnected") }
    : { Icon: WifiOff, color: "var(--warning)", text: line.reg === "reconnecting" ? t("reconnecting") : t("connecting") };

  return (
    <Box sx={{ background: "var(--surface-card)", border: "1px solid var(--line-100)", borderRadius: "20px", boxShadow: "var(--shadow-lg)",
      display: "flex", flexDirection: "column", overflow: "hidden", color: "var(--ink-800)", flex: 1, minHeight: 0 }}>
      {/* Header: my status, the line */}
      <Box component="header" sx={{ display: "flex", alignItems: "center", gap: "8px", p: "12px 12px 8px 16px" }}>
        {hasLine && (
          <>
            <ButtonBase onClick={(e) => setStatusMenu(e.currentTarget)} disabled={statusSaving} aria-haspopup="menu"
              aria-label={t("statusButton", { status: t(`status.${status}`) })}
              sx={{ gap: "8px", height: 36, px: "12px", borderRadius: "var(--r-pill)", border: "1px solid var(--line-200)", font: "600 13px/1 var(--font-ui)", color: "var(--ink-800)" }}>
              <Dot color={STATUS_COLOR[status] ?? "var(--ink-400)"} />{t(`status.${status}`)}
              {statusSaving ? <CircularProgress size={12} /> : <ChevronDown size={16} aria-hidden="true" />}
            </ButtonBase>
            <Menu anchorEl={statusMenu} open={Boolean(statusMenu)} onClose={() => setStatusMenu(null)}>
              {STATUSES.map((s) => (
                <MenuItem key={s} selected={s === status} onClick={() => { setStatusMenu(null); onStatusChange?.(s); }} sx={{ gap: 1 }}>
                  <Dot color={STATUS_COLOR[s]} />
                  <Box>
                    <Typography sx={{ font: "600 13px/1.2 var(--font-ui)" }}>{t(`status.${s}`)}</Typography>
                    <Typography sx={{ font: "500 11px/1.3 var(--font-ui)", color: "var(--ink-500)" }}>{t(`statusHelp.${s}`)}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", font: "600 12px/1 var(--font-ui)", color: "var(--ink-600)" }} title={line.regError || undefined}>
          <conn.Icon size={16} strokeWidth={1.8} aria-hidden="true" style={{ color: conn.color }} />
          <span>{hasLine ? <>{t("extShort", { ext: "" })}<span dir="ltr">{me.extension}</span> · </> : null}{conn.text}</span>
        </Box>
        <ButtonBase onClick={onMinimise} aria-label={t("minimise")} title={t("minimise")} sx={{ width: 36, height: 36, borderRadius: "10px", color: "var(--ink-500)" }}>
          <Minimize2 size={17} strokeWidth={1.8} aria-hidden="true" />
        </ButtonBase>
      </Box>

      {/* The owner without a line yet: theirs, made on a click (one extension of the business's pool). */}
      {!hasLine ? (
        me?.owner ? (
          <Box sx={{ p: "24px 20px", display: "flex", flexDirection: "column", gap: "12px", alignItems: "center", textAlign: "center" }}>
            <Box aria-hidden="true" sx={{ width: 56, height: 56, borderRadius: "50%", display: "grid", placeItems: "center", background: "var(--c-violet-bg)", color: "var(--c-violet)" }}><Phone size={24} /></Box>
            <Typography sx={{ font: "800 17px/1.3 var(--font-ui)", color: "var(--ink-900)" }}>{t("ownerLineTitle")}</Typography>
            <Typography sx={{ font: "500 13px/1.5 var(--font-ui)", color: "var(--ink-600)" }}>{t("ownerLineBody")}</Typography>
            <ButtonBase onClick={onMakeLine} disabled={making} sx={{ minHeight: 44, px: "20px", borderRadius: "12px", background: "var(--grad-brand-135)", boxShadow: "var(--shadow-cta)", color: "#fff", font: "700 14px/1 var(--font-ui)", gap: 1 }}>
              {making && <CircularProgress size={14} sx={{ color: "#fff" }} />}{t("ownerLineButton")}
            </ButtonBase>
            {makeError && <Typography role="alert" sx={{ font: "600 12px/1.4 var(--font-ui)", color: "var(--danger)" }}>{t(makeError === "no_free_extension" ? "ownerLineNoFree" : "ownerLineFailed")}</Typography>}
          </Box>
        ) : (
          <Banner icon={ShieldAlert} tone="bad" title={t("noExtension")} body={t("noExtensionBody")} />
        )
      ) : (
        <>
          {/* What keeps it from calling — found when it opens */}
          {!secure ? (
            <Banner icon={ShieldAlert} tone="bad" title={t("notSecure")} body={t("notSecureBody")} />
          ) : line.mic === "blocked" ? (
            <Banner icon={MicOff} tone="bad" title={t("micBlocked")} body={t("micBlockedBody")} action={t("checkAgain")} onAction={recheck} busy={checkingMic} />
          ) : line.mic === "none" ? (
            <Banner icon={MicOff} tone="bad" title={t("micNone")} body={t("micNoneBody")} action={t("checkAgain")} onAction={recheck} busy={checkingMic} />
          ) : (line.reg === "reconnecting" || line.reg === "failed") ? (
            <Banner icon={RefreshCw} title={t(line.reg === "failed" ? "lineFailed" : "lineReconnecting")} body={t("lineBody")} action={t("tryNow")} onAction={line.retry} />
          ) : null}

          {current ? (
            <PhoneCall line={line} current={current} displayName={current.dir === "in" ? ringName : null} onOpenTicket={onOpenTicket} />
          ) : (
            <>
              <Box sx={{ m: "8px 16px 0" }}>
                <Segments label={t("sections")} value={tab} onChange={setTab}
                  items={[{ value: "keypad", label: t("keypad") }, { value: "recent", label: t("recent"), badge: missed || 0 }, { value: "book", label: t("contacts") }]} />
              </Box>
              {tab === "keypad" && <PhoneKeypad ready={ready} onCall={dial} prefill={prefill?.number} prefillSeq={prefill?.seq} />}
              {tab === "recent" && (recent ? <PhoneRecent ready={ready} onCall={dial} data={recent} />
                : <Box sx={{ p: 4, display: "grid", placeItems: "center" }}><CircularProgress size={20} sx={{ color: "var(--brand-violet)" }} /></Box>)}
              {tab === "book" && <PhoneBook ready={ready} onCall={dial} />}
            </>
          )}
        </>
      )}
    </Box>
  );
}
